import type { GetCurrentMemberFn, SearchUsersFn } from '@/features/iam'
import type { GetAdvisorUserPublicIdFn } from '@/features/client-user-management'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { FilterStorage } from '@/infra/FiltersViewModel'
import { Id } from '@/infra/id'
import { type Storage } from '@/infra/storage'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { links } from '@/utils/link'
import { ClientUserSearchViewModel } from '@/features/client-user-management/ClientUserSearchViewModel'
import { clientUserManagementColumns } from '@/features/client-user-management/ClientUserManagement/ClientUserManagementViewModel'

/**
 * Lists the clients assigned to an advisor, on the advisor's details.
 */
export class AdvisorClientsTabViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The columns of the clients table.
   */
  readonly columns = clientUserManagementColumns

  /**
   * Searches the advisor's clients.
   */
  readonly userSearchVm

  constructor(
    getAdvisorUserPublicId: GetAdvisorUserPublicIdFn,
    getCurrentMember: GetCurrentMemberFn,
    localStorage: Storage,
    searchUsers: SearchUsersFn,
    createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.userSearchVm = new ClientUserSearchViewModel(
      searchUsers,
      getCurrentMember,
      (namespace) => new FilterStorage(localStorage, namespace),
      createAlertRetryWrapper,
      {
        filterStorageNamespace: 'AdvisorClientsTableFilters',
        getAdvisorUserPublicId,
      },
    )

    this._lifecycle.manage(this.userSearchVm)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The link to a client's details.
   */
  getUserDetailsLinkForUser(user: SearchableUser) {
    return links.clientUserDetails(Id.assert(user.publicId))
  }
}
